import { FC, useEffect, useMemo, useState } from "react";
import { IconButton, Typography, useTheme } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  OperationalHourCounter,
  formatOperationalHours,
  OperationalHoursCorrection,
  readOperationalHoursValue
} from "app/data/helpers/operationalHoursHelpers";
import { useMileagePermissions } from "../MileageCorrectionDisplay/hooks/useMileagePermissions";
import { MileageCorrectionCard } from "../MileageCorrectionDisplay/MileageCorrectionDisplay.styled";
import OperationalHoursCorrectionModal from "../OperationalHoursCorrectionModal";
import { OPERATIONAL_HOUR_COUNTER_CONFIG } from "./constants";
import { useOperationalHoursHistory } from "./hooks/useOperationalHoursHistory";
import OperationalHoursHistory from "./OperationalHoursHistory";
import {
  CounterLabel,
  CounterList,
  CounterRow,
  CounterValue,
  CounterValueGroup
} from "./OperationalHoursCorrectionDisplay.styled";

interface Props {
  specificObject: any;
  accountId: string;
  embedded?: boolean;
}

const OperationalHoursCorrectionDisplay: FC<Props> = ({
  specificObject,
  accountId,
  embedded = false
}) => {
  const { t } = useTranslation(["translation"]);
  const theme = useTheme();
  const { canEditMileage } = useMileagePermissions();
  const [activeCounter, setActiveCounter] =
    useState<OperationalHourCounter | null>(null);
  const [displayedOverrides, setDisplayedOverrides] = useState<
    Partial<Record<OperationalHourCounter, number>>
  >({});

  const objectId = specificObject?.uniqueId as string | undefined;
  const { corrections, loading, refresh, addCorrection } =
    useOperationalHoursHistory(accountId, objectId);

  useEffect(() => {
    setDisplayedOverrides({});
  }, [objectId]);

  useEffect(() => {
    setDisplayedOverrides((current) => {
      const next = { ...current };
      let changed = false;

      OPERATIONAL_HOUR_COUNTER_CONFIG.forEach((config) => {
        const override = next[config.counter];
        if (override == null) return;

        const metadataValue = readOperationalHoursValue(
          specificObject?.metadata,
          config.metadataUniqueId
        );

        if (metadataValue === override) {
          delete next[config.counter];
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [specificObject?.metadata]);

  const displayedValues = useMemo(() => {
    return OPERATIONAL_HOUR_COUNTER_CONFIG.reduce(
      (values, config) => {
        const metadataValue = readOperationalHoursValue(
          specificObject?.metadata,
          config.metadataUniqueId
        );

        values[config.counter] =
          displayedOverrides[config.counter] ?? metadataValue;

        return values;
      },
      {} as Record<OperationalHourCounter, number>
    );
  }, [displayedOverrides, specificObject?.metadata]);

  const visibleCounters = OPERATIONAL_HOUR_COUNTER_CONFIG.filter(
    (config) =>
      config.counter !== "PtoHours" || displayedValues[config.counter] > 0
  );

  const activeConfig = OPERATIONAL_HOUR_COUNTER_CONFIG.find(
    (config) => config.counter === activeCounter
  );

  const handleCorrected = (correction: OperationalHoursCorrection) => {
    setDisplayedOverrides((current) => ({
      ...current,
      [correction.counter]: correction.newDisplayed
    }));
    addCorrection(correction);
  };

  if (!objectId) return null;

  const content = (
    <>
      {!embedded && (
        <Typography
          variant="h6"
          fontWeight={600}
          color={theme.palette.text.primary}
          sx={{ fontSize: "18px", mb: 1.5 }}
        >
          {t("typography.operationalHoursCorrection")}
        </Typography>
      )}

      <CounterList data-testid="operational-hours-card">
        {visibleCounters.map((config) => {
          const formattedValue = t("typography.operationalHoursValue", {
            value: formatOperationalHours(displayedValues[config.counter])
          });

          return (
            <CounterRow key={config.counter}>
              <CounterLabel>{t(config.labelKey)}</CounterLabel>
              <CounterValueGroup>
                <CounterValue
                  data-testid={`operational-hours-value-${config.counter}`}
                >
                  {formattedValue}
                </CounterValue>
                {canEditMileage && (
                  <IconButton
                    size="small"
                    aria-label={t("typography.correctOperationalHours", {
                      counter: t(config.labelKey)
                    })}
                    data-testid={`operational-hours-edit-${config.counter}`}
                    onClick={() => setActiveCounter(config.counter)}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </CounterValueGroup>
            </CounterRow>
          );
        })}
      </CounterList>

      <OperationalHoursHistory
        accountId={accountId}
        corrections={corrections}
        loading={loading}
      />

      {activeConfig && (
        <OperationalHoursCorrectionModal
          isOpen
          onClose={() => setActiveCounter(null)}
          counter={activeConfig.counter}
          counterLabel={t(activeConfig.labelKey)}
          currentValue={displayedValues[activeConfig.counter]}
          specificObject={specificObject}
          accountId={accountId}
          onCorrected={handleCorrected}
          onHistoryRefresh={refresh}
        />
      )}
    </>
  );

  if (embedded) return content;

  return <MileageCorrectionCard>{content}</MileageCorrectionCard>;
};

export default OperationalHoursCorrectionDisplay;
